import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { TextTooltip } from 'components/Tooltip';
import { useTruncateText } from 'components/TruncatedText/hooks/useTruncatedText';
import { contentPropsCls, tooltipCls } from 'components/TruncatedText/TruncatedText.styles';
import { debounce } from 'lodash';

type TruncatedTextProps = {
  customContainerWidth?: number;
  customFontSize?: string;
  endCharsCount?: number;
  text?: string;
  showTooltip?: boolean;
  setFullTextWidth?: (value: number) => void;
} & HTMLAttributes<HTMLSpanElement>;

export const TruncatedText = ({
  customContainerWidth,
  customFontSize,
  endCharsCount = 3,
  showTooltip = true,
  text,
  setFullTextWidth,
  ...props
}: TruncatedTextProps) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { truncatedText, shouldTruncate } = useTruncateText(
    containerWidth,
    text ?? '',
    endCharsCount,
    customContainerWidth,
    customFontSize,
    setFullTextWidth,
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver(
        debounce((entries) => {
          for (const entry of entries) {
            setContainerWidth(entry.contentRect.width);
          }
        }, 10),
      );

      observer.observe(containerRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  if (!text) return null;

  return (
    <span ref={containerRef} {...props}>
      {shouldTruncate && showTooltip ? (
        <TextTooltip
          className={tooltipCls}
          text={text}
          showArrow
          initialState={{ placement: 'top' }}
          arrowProps={{ className: 'darkArrow' }}
          contentProps={{ className: contentPropsCls }}
        >
          {truncatedText}
        </TextTooltip>
      ) : shouldTruncate && !showTooltip ? (
        truncatedText
      ) : (
        text
      )}
    </span>
  );
};
